import {
  cloneElement,
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cx } from '../cx';

const control =
  'w-full border-2 border-gu-line bg-gu-sunken px-3 text-body text-gu-ink placeholder:text-gu-ink-muted ' +
  'transition-colors duration-150 hover:border-gu-ink-muted focus:border-gu-ink focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-gu-focus/40 disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-[invalid=true]:border-gu-danger';

/** As classes do controlo, para quem precise de um input fora destes componentes. */
export const controlClass = control;

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cx(control, 'min-h-11', className)} {...props} />;
  },
);

export type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Nome do botao para o leitor de ecra; o estado vai em aria-pressed. */
  toggleLabel?: string;
};

/**
 * Campo de password com um botao «olho» que mostra e volta a esconder o texto.
 * O botao e alcancavel pelo teclado e diz ao leitor de ecra se a password esta
 * visivel. Nao usar para o PIN dos jovens nos PCs da Arena (espaco publico).
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className, toggleLabel = 'Mostrar password', ...props }, ref) {
    const [visible, setVisible] = useState(false);
    return (
      <div className="relative">
        <input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cx(control, 'min-h-11 pr-12', className)}
          {...props}
        />
        <button
          type="button"
          aria-label={toggleLabel}
          aria-pressed={visible}
          aria-controls={props.id}
          disabled={props.disabled}
          title={visible ? 'Esconder' : 'Mostrar'}
          onClick={() => {
            setVisible((value) => !value);
          }}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gu-ink-muted transition-colors hover:text-gu-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gu-focus disabled:cursor-not-allowed disabled:opacity-50"
        >
          <EyeIcon crossed={visible} />
        </button>
      </div>
    );
  },
);

/** Olho aberto quando a password esta escondida; riscado quando esta a vista. */
function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {crossed ? <path d="M3 3l18 18" /> : null}
    </svg>
  );
}

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...props }, ref) {
    return <select ref={ref} className={cx(control, 'min-h-11 pr-8', className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 4, ...props }, ref) {
  return <textarea ref={ref} rows={rows} className={cx(control, 'py-2', className)} {...props} />;
});

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: ReactNode;
  hint?: ReactNode;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, id, ...props },
  ref,
) {
  const generated = useId();
  const inputId = id ?? generated;
  const hintId = hint ? `${inputId}-hint` : undefined;
  return (
    <div className={cx('flex items-start gap-3', className)}>
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        aria-describedby={hintId}
        className="mt-0.5 size-5 shrink-0 cursor-pointer accent-[rgb(var(--gu-accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus focus-visible:ring-offset-2 focus-visible:ring-offset-gu-ground"
        {...props}
      />
      <div className="flex flex-col gap-0.5">
        <label htmlFor={inputId} className="cursor-pointer text-body">
          {label}
        </label>
        {hint ? (
          <span id={hintId} className="text-caption text-gu-ink-muted">
            {hint}
          </span>
        ) : null}
      </div>
    </div>
  );
});

type ControlElement = ReactElement<{
  id?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
}>;

export type FieldProps = {
  label: ReactNode;
  /** Um `Input`, `Select` ou `Textarea`; recebe o id, a descricao e o estado de erro. */
  children: ControlElement;
  hint?: ReactNode;
  /** Mensagem de erro; quando existe, o controlo fica marcado como invalido. */
  error?: ReactNode;
  /** Mostra «(opcional)» junto ao rotulo. Os campos sao obrigatorios por defeito. */
  optional?: boolean;
  className?: string;
};

/**
 * Rotulo, ajuda e erro ligados ao controlo (htmlFor, aria-describedby,
 * aria-invalid), para que o leitor de ecra leia tudo junto.
 */
export function Field({ label, children, hint, error, optional, className }: FieldProps) {
  const generated = useId();
  const id = children.props.id ?? generated;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [children.props['aria-describedby'], hintId, errorId].filter(Boolean).join(' ') || undefined;

  const control = cloneElement(children, {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : children.props['aria-invalid'],
  });

  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="font-sub text-label font-bold uppercase text-gu-ink-muted">
        {label}
        {optional ? <span className="ml-1 normal-case tracking-normal">(opcional)</span> : null}
      </label>
      {control}
      {hint ? (
        <span id={hintId} className="text-caption text-gu-ink-muted">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="text-caption font-semibold text-gu-danger">
          <span aria-hidden="true">✕ </span>
          {error}
        </span>
      ) : null}
    </div>
  );
}
